import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlanBadge } from '../components/PlanBadge';
import { ApiKeyDisplay } from '../components/ApiKeyDisplay';
import { addPlugin } from '../api/portalApi';
import type { CustomerProfile, License } from '../api/portalApi';
import styles from './Dashboard.module.css';

const ALL_PLUGINS = [
  { id: 'message-center', name: 'Message Center' },
  { id: 'admin-center', name: 'Admin Center' },
  { id: 'debug-cloud', name: 'Debug Cloud' },
];

interface DashboardProps {
  customer: CustomerProfile;
  licenses: License[];
  onReload: () => void;
}

export function Dashboard({ customer, licenses, onReload }: DashboardProps) {
  const activeLicenses = licenses.filter((l) => l.isActive);
  const ownedPlugins = licenses.flatMap((l) => l.plugins);
  const availablePlugins = ALL_PLUGINS.filter((p) => !ownedPlugins.includes(p.id));

  const [showAddPlugin, setShowAddPlugin] = useState(false);
  const [adding, setAdding] = useState(false);

  const handleAddPlugin = async (pluginId: string) => {
    setAdding(true);
    try {
      await addPlugin(pluginId);
      setShowAddPlugin(false);
      onReload();
    } catch (err) {
      console.error('Failed to add plugin:', err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <h1 className={styles.greeting}>Welcome, {customer.name.split(' ')[0]}</h1>

      {activeLicenses.length === 0 ? (
        <div className={styles.emptyCard}>
          <h3>No active licenses</h3>
          <p>Your trial may have expired. <Link to="/upgrade">Upgrade</Link> to continue.</p>
        </div>
      ) : (
        <div className={styles.licenseCards}>
          {activeLicenses.map((lic) => (
            <div key={lic.apiKey} className={styles.licenseCard}>
              <div className={styles.licenseHeader}>
                <span className={styles.pluginName}>{lic.plugins.join(', ')}</span>
                <PlanBadge plan={lic.plan} />
              </div>
              <ApiKeyDisplay apiKey={lic.apiKey} />
              <div className={styles.licenseMeta}>
                <span>Expires: {lic.expiresAt === '9999-12-31T23:59:59.999Z' ? 'Never' : new Date(lic.expiresAt).toLocaleDateString()}</span>
                <span>Domains: {lic.domains.length > 0 ? lic.domains.join(', ') : 'Any'} ({lic.maxDomains === 0 ? 'unlimited' : `${lic.domains.length}/${lic.maxDomains}`})</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {availablePlugins.length > 0 && (
        <div className={styles.addPluginSection}>
          {!showAddPlugin ? (
            <button className={styles.addPluginBtn} onClick={() => setShowAddPlugin(true)}>
              + Add Another Plugin
            </button>
          ) : (
            <div className={styles.addPluginPicker}>
              <p className={styles.addPluginLabel}>Choose a plugin to add (14-day free trial):</p>
              <div className={styles.addPluginGrid}>
                {availablePlugins.map((p) => (
                  <button
                    key={p.id}
                    className={styles.addPluginCard}
                    onClick={() => handleAddPlugin(p.id)}
                    disabled={adding}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              <button className={styles.addPluginCancel} onClick={() => setShowAddPlugin(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <div className={styles.quickLinks}>
        <h2>Quick Links</h2>
        <div className={styles.linkGrid}>
          <Link to="/getting-started" className={styles.linkCard}>
            <strong>Getting Started</strong>
            <span>Install your plugin and start building</span>
          </Link>
          <Link to="/licenses" className={styles.linkCard}>
            <strong>Manage Licenses</strong>
            <span>View all your API keys and plans</span>
          </Link>
          <Link to="/css" className={styles.linkCard}>
            <strong>CSS Editor</strong>
            <span>Customize your plugin's appearance</span>
          </Link>
          <Link to="/upgrade" className={styles.linkCard}>
            <strong>Upgrade</strong>
            <span>Get more features and higher limits</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
